import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {getInstallExperience, isIosDevice, isStandaloneMode} from '../lib/pwa/installExperience';

const PwaInstallContext = createContext(null);

export function PwaInstallProvider({children}) {
  const [promptEvent, setPromptEvent] = useState(null);
  const [standalone, setStandalone] = useState(() => isStandaloneMode());
  const ios = useMemo(() => isIosDevice(), []);

  useEffect(() => {
    const displayMode = window.matchMedia?.('(display-mode: standalone)');
    const updateStandalone = () => setStandalone(isStandaloneMode());
    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setPromptEvent(event);
    };
    const handleInstalled = () => {
      setStandalone(true);
      setPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    displayMode?.addEventListener?.('change', updateStandalone);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
      displayMode?.removeEventListener?.('change', updateStandalone);
    };
  }, []);

  const requestInstall = useCallback(async () => {
    if (!promptEvent) return {outcome: 'unavailable'};

    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setPromptEvent(null);
    return choice;
  }, [promptEvent]);

  const value = useMemo(() => ({
    experience: getInstallExperience({
      standalone,
      hasPrompt: Boolean(promptEvent),
      ios
    }),
    requestInstall
  }), [ios, promptEvent, requestInstall, standalone]);

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  const value = useContext(PwaInstallContext);
  if (!value) throw new Error('usePwaInstall must be used within PwaInstallProvider.');
  return value;
}
